import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import List from '@material-ui/core/List';
import { type Currency, FeatureFlag, License } from '@ps/web-core-enums';
import { useTrackers } from '@ps/web-shared-analytics';
import type { TrackedTranslation } from '@ps/web-shared-components';
import { Translation, useTrackingLabel } from '@ps/web-shared-components';
import classNames from 'clsx';
import { differenceInCalendarDays, isSameMonth } from 'date-fns';
import React, { useCallback, useState } from 'react';
import type { FieldInputProps } from 'react-final-form';
import { Field, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import ToggleArrow from '@swf/core/assets/icons/arrow.svg';
import WarningIcon from '@swf/core/assets/icons/warning-triangle.svg';
import ErrorIcon from '@swf/core/assets/icons/warning.svg';
import type { Country } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import useRequest from '@swf/core/hooks/useRequest';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import type { FieldNameProp } from '@swf/core/types/Props';

import type {
  FirstWizardData,
  PaySystem,
  PaySystemDetails,
  PaySystemDetailsResponse,
  SavedCard
} from '../../types/Cashier';
import { CardType } from '../../types/cardType';
import { getCreditTime } from '../../utils/cashier';
import PaySystemInfoPrompt from '../Deposit/components/DepositMethod/components/PaySystemInfoPrompt/PaySystemInfoPrompt';
import ListItem from '../ListItem/ListItem';
import PaymentMethodLogo from '../PaymentMethodLogo/PaymentMethodLogo';

import {
  TXTWCL_Card_unavailable,
  TXTWCL_Minimum_N0,
  TXTWCL_Your_card_will_expire_in_1_day,
  TXTWCL_Your_card_will_expire_in_N0_da_ELL,
  TXTWCL_Your_card_will_expire_today
} from './PaymentMethodSelect.messages.json';
import {
  accordion,
  accordionBonusReady,
  accordionDetails,
  accordionDisabled,
  accordionExpanded,
  accordionSummary,
  accordionSummaryContent,
  accordionSummaryDisabled,
  accordionSummaryExpanded,
  avatar,
  avatarContainer,
  button,
  daysLeftAlert,
  errorIcon,
  list,
  listItem,
  minimum,
  primaryText,
  secondaryText,
  unavailable,
  warningIcon
} from './PaymentMethodSelect.scss';

interface Props extends FieldNameProp {
  disable?: boolean;
  heading?: TrackedTranslation;
  initialValue?: CardType;
  isSavedCard?: boolean;
  options: (PaySystem | SavedCard)[];
  variant?: 'bonusReady';
  onClick?: (callback: () => void) => void;
  setNativeBonusVisibility?: () => void;
  isButton?: boolean;
}

interface OptionType {
  cardInfoId?: number;
  cardType: CardType;
  daysLeft?: number;
  hostileBin?: boolean;
  i18nSystemName?: string;
  maskedCardNum?: string;
  once: number;
  paySystem?: CardType;
  productId?: string;
  systemName?: PaySystem['systemName'];
}

const mapOption = (
  option: SavedCard | PaySystem,
  data: FirstWizardData,
  isSavedCard?: boolean
): OptionType => {
  if (isSavedCard) {
    const {
      savedCard: {
        cardInfoId,
        expMonth,
        expYear,
        hostileBin,
        maskedCardNum,
        paySystem,
        specificData
      }
    } = option as SavedCard;

    const { systemName, i18nSystemName, once } = data.paySystems?.find(
      ({ cardType }) => cardType === paySystem
    ) as PaySystem;

    const card: OptionType = {
      cardInfoId,
      cardType: paySystem,
      hostileBin,
      i18nSystemName:
        (paySystem === CardType.OpenBanking && specificData?.[0].bankName) || i18nSystemName,
      maskedCardNum,
      once,
      systemName,
      productId: specificData?.[0].bankId
    };

    if (expMonth && expYear) {
      const expDate = new Date(expYear, expMonth, 0);
      const today = new Date();

      if (isSameMonth(expDate, today)) {
        card.daysLeft = differenceInCalendarDays(expDate, today);
      }
    }

    return card;
  } else {
    const { i18nSystemName, cardType, once, systemName } = option as PaySystem;
    return {
      cardType,
      i18nSystemName,
      once,
      systemName
    };
  }
};

const getDaysLeftMessage = (daysLeft: number) => {
  if (daysLeft === 0) {
    return <Translation {...TXTWCL_Your_card_will_expire_today} />;
  }
  if (daysLeft === 1) {
    return <Translation {...TXTWCL_Your_card_will_expire_in_1_day} />;
  }

  return <Translation {...TXTWCL_Your_card_will_expire_in_N0_da_ELL} values={{ 0: daysLeft }} />;
};

const getSecondaryContext = (
  showMinimumAmount: boolean,
  showCreditTime: boolean,
  option: PaySystem | SavedCard,
  data: FirstWizardData,
  currency?: Currency,
  isSavedCard?: boolean,
  country?: Country
) => {
  let creditTimeMessage;
  const { cardType, daysLeft, hostileBin, once } = mapOption(option, data, isSavedCard);
  if (showCreditTime) {
    creditTimeMessage = getCreditTime(cardType, country);
  }
  if (isSavedCard && hostileBin) {
    return (
      <span className={unavailable}>
        <ErrorIcon className={errorIcon} />
        <Translation {...TXTWCL_Card_unavailable} />
      </span>
    );
  }
  if (showMinimumAmount || showCreditTime || Number.isInteger(daysLeft)) {
    return (
      <>
        <span className={minimum}>
          {showMinimumAmount && (
            <Translation
              {...TXTWCL_Minimum_N0}
              values={{
                0: (
                  <CurrencyAmount
                    value={once}
                    currency={currency}
                    currencyDisplay="code"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                    dollarValue
                  />
                )
              }}
            />
          )}
          {showMinimumAmount && creditTimeMessage && ' | '}
          {creditTimeMessage && <Translation {...creditTimeMessage} />}
        </span>
        {Number.isInteger(daysLeft) && (
          <span className={daysLeftAlert}>
            <WarningIcon className={warningIcon} />
            {getDaysLeftMessage(daysLeft as number)}
          </span>
        )}
      </>
    );
  }
  return null;
};

const PaymentMethodSelect: React.FC<Props> = ({
  disable,
  heading,
  fieldName,
  initialValue,
  options,
  variant,
  isSavedCard,
  isButton = true,
  onClick,
  setNativeBonusVisibility
}) => {
  const { data } = useWizardContext();

  const request = useRequest();
  const form = useForm();
  const currency = useSelector(select.account.getCurrency);
  const license = useSelector(select.account.getLicense);
  const isRomanianTemporaryAccount = useSelector(select.account.isRomanianTemporaryAccount);
  const showMinimumAmount = useFeature(FeatureFlag.SwcDepositMethodMinimum);
  const showCreditTime = useFeature(FeatureFlag.SwcCreditTime);
  const { inputFilled } = useTrackers();
  const trackingName = useTrackingLabel(heading) as string;
  const [expanded, setExpanded] = useState(disable);
  const [showInfoPrompt, setShowInfoPrompt] = useState(false);
  const [paySystemDetails, setPaySystemDetails] = useState<PaySystemDetails>();
  const country = useSelector(select.account.getCountry) as Country;

  const handleChange = (e: React.ChangeEvent<object>, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const showPaysystemPrompt = useCallback(
    (paySystemDetails: PaySystemDetails, hostileBin?: boolean) => {
      if (
        paySystemDetails?.msg &&
        paySystemDetails.available &&
        !hostileBin &&
        !(license === License.IT && paySystemDetails.authenticationRequired) &&
        !(isRomanianTemporaryAccount && !paySystemDetails.remainingLimit)
      ) {
        const localStorageMessages = localStorage.getItem('paysystemMessages');
        const savedMessages = localStorageMessages && JSON.parse(localStorageMessages);

        if (savedMessages && savedMessages[paySystemDetails.type]) {
          if (savedMessages[paySystemDetails.type] !== paySystemDetails?.msg) {
            delete savedMessages[paySystemDetails.type];
            localStorage.setItem('paysystemMessages', JSON.stringify(savedMessages));
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      } else {
        return false;
      }
    },
    [isRomanianTemporaryAccount, license]
  );

  const handleClick = useCallback(
    async (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      input: FieldInputProps<any, HTMLInputElement>,
      cardType: CardType,
      strInput?: string,
      cardInfoId?: number,
      hostileBin?: boolean
    ) => {
      if (!isButton) {
        return null;
      }
      input.onChange(cardType);
      if (isSavedCard) {
        data.savedCardInfoId = cardInfoId;
      } else {
        data.regularMethodSelected = true;
      }

      const {
        body: { paySystemDetails: response }
      } = await lastValueFrom(
        request<PaySystemDetailsResponse>({
          service: 'wc3.paysystem.get.paySystemDetails',
          body: { currency, type: cardType }
        })
      );

      const callback = () => {
        if (showPaysystemPrompt(response, hostileBin)) {
          setPaySystemDetails(response);
          setShowInfoPrompt(true);
        } else {
          form.submit();
        }
      };

      inputFilled({ fieldName: trackingName, fieldType: 'dropdown', strInput });
      setNativeBonusVisibility?.();

      if (onClick) {
        onClick(callback);
      } else {
        callback();
      }
    },
    [
      isButton,
      isSavedCard,
      request,
      currency,
      inputFilled,
      trackingName,
      setNativeBonusVisibility,
      onClick,
      data,
      showPaysystemPrompt,
      form
    ]
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      classes={{
        root: classNames(accordion, variant === 'bonusReady' && accordionBonusReady),
        expanded: accordionExpanded,
        disabled: accordionDisabled
      }}
      disabled={disable}
    >
      {heading && (
        <AccordionSummary
          expandIcon={!disable && <ToggleArrow />}
          classes={{
            root: accordionSummary,
            expanded: accordionSummaryExpanded,
            content: accordionSummaryContent,
            disabled: accordionSummaryDisabled
          }}
        >
          <Translation {...heading} />
        </AccordionSummary>
      )}

      {showInfoPrompt
        ? paySystemDetails && (
            <PaySystemInfoPrompt
              paySystemDetails={paySystemDetails}
              callback={() => setShowInfoPrompt(false)}
            />
          )
        : null}

      <AccordionDetails classes={{ root: accordionDetails }}>
        <List classes={{ root: list }}>
          <Field name={`${fieldName}.type`} initialValue={initialValue}>
            {({ input }) =>
              options.map((option, index) => {
                const {
                  cardType,
                  systemName,
                  i18nSystemName,
                  maskedCardNum,
                  cardInfoId,
                  hostileBin,
                  productId
                } = mapOption(option, data, isSavedCard);
                return (
                  <ListItem
                    key={`${cardType}.${index}`}
                    classes={{
                      button,
                      avatarContainer,
                      avatar,
                      primaryText,
                      root: listItem,
                      secondaryText
                    }}
                    onClick={() => handleClick(input, cardType, systemName, cardInfoId, hostileBin)}
                    avatar={
                      <PaymentMethodLogo
                        cardType={cardType}
                        i18nSystemName={i18nSystemName}
                        productId={productId}
                      />
                    }
                    primary={
                      isSavedCard && maskedCardNum
                        ? `${i18nSystemName} ${maskedCardNum}`
                        : i18nSystemName
                    }
                    secondary={getSecondaryContext(
                      showMinimumAmount,
                      showCreditTime,
                      option,
                      data,
                      currency,
                      isSavedCard,
                      country
                    )}
                    isButton={isButton}
                  />
                );
              })
            }
          </Field>
        </List>
      </AccordionDetails>
    </Accordion>
  );
};

export default PaymentMethodSelect;
